import React from 'react'
import cx from 'classnames'
import { useAppSelector } from 'reduxTyped'
import { BtnV2, l, TextArea } from '@manychat/manyui'

import { useQuestionnaire } from 'apps/aiAutomation/components/TovSetup/components/Questionnaire/hooks/useQuestionnaire'
import { getInstagramChannel } from 'common/core/selectors/channelSelectors'
import { TovReaction } from 'shared/api/requests/aiToneOfVoice/schemas'

import { Comment } from './components/Comment/Comment'
import { useCommentsSwitching } from './hooks/useCommentsSwitching'

import cm from './Questionnaire.module.css'

export const Questionnaire = ({
  onFinish,
  onProgress,
}: {
  onFinish: (answers: TovReaction[]) => void
  onProgress: (completedCount: number) => void
}) => {
  const { avatar } = useAppSelector(getInstagramChannel)

  const {
    samples,
    form,
    submitLike,
    submitDislike,
    showCommentForm,
    answers,
    setShowCommentForm,
    activeSample,
    reset,
  } = useQuestionnaire({ onFinish })

  const {
    commentsRef,
    commentsWrapperRef,

    samplesRef,
    samplesWrapperRef,
  } = useCommentsSwitching({ activeSample })

  const handleSubmitLike = () => {
    submitLike()
    onProgress(answers.length + 1)
  }

  const handleSubmitDislike = (data: { user_reaction: string }) => {
    submitDislike(data)
    onProgress(answers.length + 1)
  }

  const { formState, register, handleSubmit } = form

  const getCommentClassName = (index: number) =>
    cx(cm.commentWrapper, {
      [cm.commentWrapper_visible]: index <= activeSample,
      [cm.commentWrapper_liked]: answers[index] && answers[index].is_reaction_positive,
      [cm.commentWrapper_disliked]: answers[index] && !answers[index].is_reaction_positive,
    })

  const onCancelCommentForm = () => {
    setShowCommentForm(false)
    reset()
  }

  return (
    <>
      <h4 className="m-b-xs text-subheading">{l.translate('Review AI’s draft replies')}</h4>

      <p aria-live="polite" aria-atomic="true" className="m-b-lg text-sm text-secondary">
        {l.translate('{current} of {total} replies', {
          current: activeSample + 1,
          total: samples.length,
        })}
      </p>

      <section className={cm.questionnaire}>
        <p className="m-b-sm font-semibold">{l.translate('To a comment like this')}</p>

        <div className="p-relative" ref={commentsWrapperRef} aria-live="polite">
          {samples.map((sample, index) => (
            <div
              key={index}
              className={getCommentClassName(index)}
              ref={(element) => {
                if (element) {
                  commentsRef.current[index] = element
                }
              }}
              aria-hidden={index > activeSample}
              // @ts-expect-error type for inert is supported starting from React 19+
              // It's also not working for now when we pass true/false, that's why there is an empty string and undefined values
              inert={index > activeSample ? '' : undefined}
            >
              <Comment
                avatar={sample.comment.avatar_url}
                postPreview={sample.post.media_url}
                comment={
                  <span>
                    {l.translate('<span class="font-semibold">{username}</span> commented:', {
                      username: sample.comment.username ?? 'Someone',
                    })}{' '}
                    {sample.comment.text}
                  </span>
                }
                variant="default"
              />
            </div>
          ))}
        </div>

        <p className="m-t-lg m-b-sm font-semibold">{l.translate('AI would reply')}</p>

        <div className={cm.replyWrapper}>
          <div className="p-relative" ref={samplesWrapperRef} aria-live="polite">
            {samples.map((sample, index) => (
              <div
                key={index}
                className={getCommentClassName(index)}
                ref={(element) => {
                  if (element) {
                    samplesRef.current[index] = element
                  }
                }}
                aria-hidden={index > activeSample}
                // @ts-expect-error type for inert is supported starting from React 19+
                // It's also not working for now when we pass true/false, that's why there is an empty string and undefined values
                inert={index > activeSample ? '' : undefined}
              >
                <Comment avatar={avatar} comment={sample.ai_reply.text} variant="light" />
              </div>
            ))}
          </div>

          {showCommentForm && (
            <form className="m-t-md d-flex flex-col" onSubmit={handleSubmit(handleSubmitDislike)}>
              <TextArea
                label={l.translate('How can we make it sound more like you?')}
                placeholder={l.translate('Describe what feels off')}
                minRows={2}
                maxRows={6}
                autoFocus
                {...register('user_reaction')}
                invalid={Boolean(formState.errors.user_reaction?.message)}
                errorText={formState.errors.user_reaction?.message}
              />

              <div className="d-flex justify-between m-t">
                <BtnV2 onClick={onCancelCommentForm}>{l.translate('Cancel')}</BtnV2>

                <BtnV2 variant="primary" type="submit">
                  {l.translate('Submit')}
                </BtnV2>
              </div>
            </form>
          )}

          {!showCommentForm && (
            <ul className={cm.questionnaire__controls} aria-label={l.translate('Review proposal')}>
              <li>
                <BtnV2 onClick={() => setShowCommentForm(true)} leftIcon="ThumbDown">
                  {l.translate('Not my style')}
                </BtnV2>
              </li>

              <li>
                <BtnV2 onClick={handleSubmitLike} leftIcon="ThumbUp">
                  {l.translate('Looks good')}
                </BtnV2>
              </li>
            </ul>
          )}

          <p className="m-t-md text-sm text-secondary">
            {l.translate('Your feedback helps AI sound more natural and true to your style.')}
          </p>
        </div>
      </section>
    </>
  )
}
